import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocInlineDate,
  DocInlineChips,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DocSignatures,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type AresPickResult,
  type CompanyPickResult,
} from '../../components/ares';
import { StavbaPickerButton, SaveStavbaButton, type StavbaPickResult } from '../../components/stavba';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import { useSaveDocument } from '../../api/hooks';
import styles from './PozarniDohledPage.module.css';

/**
 * Požární dohled — zvláštní požárně bezpečnostní opatření a záznam
 * o požárním dohledu při svařování a jiných pracích s otevřeným ohněm.
 *
 * Osnova vychází z vyhlášky č. 87/2000 Sb. (ověřeno na zakonyprolidi.cz,
 * aktuální znění = původní znění účinné od 1. 7. 2000):
 *   • příloha č. 1 písm. a)–o) — rozsah zvláštních opatření (místo, úkol,
 *     technologie, svářeči, časy, opatření, forma dohledu, seznámení,
 *     hasební prostředky, ohlášení skončení, uložení soupravy, vydavatel,
 *     potvrzování úkonů podpisem s datem a časem),
 *   • § 4 — požární dohled (nejkratší doba 8 h, předem určená osoba,
 *     výjimky v odst. 3),
 *   • § 5 odst. 5 — hasicí přístroje na přechodném pracovišti,
 *   • § 5 odst. 8 — ochranné pásmo podle výšky svařování.
 * Všechna pole jsou nepovinná (konvence aplikace).
 */

const DRAFT_KEY = 'pozarni-dohled';
const PATH = '/app/bozp/pozarni-dohled';
const MIN_DOBA_H = 8;

// === Číselníky ===

const DRUHY: Array<{ id: string; label: string }> = [
  { id: 'svarovani', label: 'Svařování / tepelné dělení kovů' },
  { id: 'natavovani', label: 'Natavování izolačních pásů hořákem' },
  { id: 'zivice', label: 'Nahřívání živic v tavných nádobách' },
  { id: 'pajeni', label: 'Pájení (pájecí lampa, el. pájedlo)' },
  { id: 'jine', label: 'Jiná práce s otevřeným ohněm' },
];

const TECHNOLOGIE_DEFAULT: Array<{ id: string; label: string }> = [
  { id: 'plamen', label: 'Svařování plamenem' },
  { id: 'rezani', label: 'Řezání kyslíkem' },
  { id: 'oblouk', label: 'Elektrický oblouk' },
  { id: 'plazma', label: 'Plazma' },
  { id: 'horak', label: 'Plynový hořák (propan)' },
  { id: 'lampa', label: 'Pájecí lampa' },
  { id: 'pajedlo', label: 'Elektrické pájedlo' },
  { id: 'tavna', label: 'Tavná nádoba' },
];

const ROLE: readonly string[] = ['svářeč', 'požární dohled', 'pomocník', 'odpovědná osoba', 'jiná'];

const OPATRENI_DEFAULT = [
  'Hořlavé látky odstraněny z ochranného pásma, případně bezpečně izolovány',
  'Hořlavé konstrukce a otvory překryty a utěsněny nehořlavým materiálem (bez nechráněných spár)',
  'Zkontrolovány přilehlé prostory — nad, pod a vedle pracoviště',
  'Změřena koncentrace hořlavých látek (přístroj, hodnota, čas)',
  'Tlakové lahve zajištěny proti pádu a v bezpečné vzdálenosti od otevřeného ohně',
  'Na pracovišti připraveny hasicí přístroje a další hasební prostředky',
  'Volný přístup k hasebním prostředkům a únikovým cestám',
];

const HASIVA_DEFAULT =
  'Min. 2 přenosné hasicí přístroje s vhodnou náplní, z toho 1× práškový o hmotnosti hasební látky min. 5 kg (§ 5 odst. 5)';

const POVINNOSTI_DEFAULT =
  'Po celou dobu dohledu sledovat svářečské pracoviště a přilehlé prostory (nad, pod a vedle). ' +
  'Kontrolovat, zda nedochází k doutnání, zvýšení teploty nebo vzniku požáru. ' +
  'Při vzniku požáru provést prvotní hasební zásah, vyhlásit požární poplach a přivolat HZS (150 / 112). ' +
  'Každou kontrolu zapsat do záznamu níže s datem, časem a podpisem; ' +
  'předání dohledu jiné osobě potvrdí podpisem předávající i přebírající.';

const ZAVER_DEFAULT = 'Pracoviště a přilehlé prostory bez známek požáru — požární dohled ukončen.';

const VYJIMKY: Array<{ id: string; label: string }> = [
  { id: '', label: 'Neuplatňuje se — dohled po skončení se vykoná' },
  { id: 'eps_shz', label: '§ 4 odst. 3 a) — provozuschopná EPS a SHZ' },
  { id: 'eps_osoba', label: '§ 4 odst. 3 a) — jen EPS + na místě osoba schopná prvotního hasebního zásahu' },
  { id: 'stale', label: '§ 4 odst. 3 b) — stálé svářečské pracoviště bez kontaktu žhavých částic s hořlavinami' },
];

// === Typy ===

interface UcastnikRow {
  id: string;
  role: string;
  jmeno: string;
  doklad: string;
  firma: string;
  podpis: string;
}
interface OpatreniRow {
  id: string;
  text: string;
  splneno: boolean;
  provedl: string;
  kontroloval: string;
}
interface KontrolaRow {
  id: string;
  datum: string;
  cas: string;
  misto: string;
  zjisteni: string;
  opatreni: string;
  podpis: string;
}
interface Chip {
  id: string;
  label: string;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}
function todayCz(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${d.getFullYear()}`;
}
function nowHm(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function emptyUcastnik(role = 'svářeč'): UcastnikRow {
  return { id: newId('u'), role, jmeno: '', doklad: '', firma: '', podpis: '' };
}
function opatreniRow(text: string): OpatreniRow {
  return { id: newId('o'), text, splneno: false, provedl: '', kontroloval: '' };
}
function emptyKontrola(): KontrolaRow {
  return { id: newId('k'), datum: '', cas: '', misto: '', zjisteni: '', opatreni: '', podpis: '' };
}
function deriveInitials(name: string): string {
  return name
    .replace(/\b(s\.?\s*r\.?\s*o\.?|a\.?\s*s\.?|spol\.?)\b/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
function num(s: string): number | null {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
function fmtNum(n: number): string {
  return n.toLocaleString('cs-CZ', { maximumFractionDigits: 1 });
}

/** „dd. mm. rrrr" + „hh:mm" → Date (nebo null, když něco chybí). */
function parseDateTime(datum: string, cas: string): Date | null {
  const d = datum.match(/^\s*(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})\s*$/);
  const t = cas.match(/^\s*(\d{1,2})[:.](\d{2})\s*$/);
  if (!d || !t) return null;
  const dt = new Date(+d[3], +d[2] - 1, +d[1], +t[1], +t[2]);
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function fmtDateTime(dt: Date): string {
  return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(
    dt.getMinutes(),
  ).padStart(2, '0')}`;
}

/**
 * Ochranné pásmo dle § 5 odst. 8 vyhl. 87/2000 Sb. — poloměr kruhu [m]:
 * od 2 m výšky min. 10 m; +0,3 m na každý další 1 m do 7 m; +0,1 m na každý
 * další 1 m do 20 m. Nad 20 m individuálně.
 */
function ochrannePasmo(vyska: number): { r: number | null; nad20: boolean; pod2: boolean } {
  if (vyska < 2) return { r: null, nad20: false, pod2: true };
  if (vyska > 20) return { r: null, nad20: true, pod2: false };
  const r = 10 + 0.3 * (Math.min(vyska, 7) - 2) + 0.1 * Math.max(0, vyska - 7);
  return { r: Math.round(r * 10) / 10, nad20: false, pod2: false };
}

export function PozarniDohledPage() {
  const { activeStavba, setActiveStavbaId } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const toast = useToast();
  const saveDoc = useSaveDocument();
  const [emailOpen, setEmailOpen] = useState(false);

  // === 01 Identifikace ===
  const [cislo, setCislo] = useState('');
  const [datum, setDatum] = useState(todayCz());
  const [stavba, setStavba] = useState({ nazev: '', adresa: '', zakazka: '' });
  const [zhotovitel, setZhotovitel] = useState({ firma: '', ico: '', sidlo: '', tel: '', email: '' });

  // === 02 Místo a pracovní úkol ===
  const [druhy, setDruhy] = useState<string[]>(['svarovani']);
  const [misto, setMisto] = useState('');
  const [ukol, setUkol] = useState('');
  const [techVlastni, setTechVlastni] = useState<Chip[]>([]);
  const [technologie, setTechnologie] = useState<string[]>([]);
  const [pracoviste, setPracoviste] = useState<'prechodne' | 'stale'>('prechodne');
  const [vyska, setVyska] = useState('');

  // === 03 Doba prací ===
  const [zahajeniDatum, setZahajeniDatum] = useState('');
  const [zahajeniCas, setZahajeniCas] = useState('');
  const [ukonceniDatum, setUkonceniDatum] = useState('');
  const [ukonceniCas, setUkonceniCas] = useState('');
  const [ohlasenoKomu, setOhlasenoKomu] = useState('');

  // === 04 Účastníci ===
  const [ucastnici, setUcastnici] = useState<UcastnikRow[]>(() => [
    emptyUcastnik('svářeč'),
    emptyUcastnik('požární dohled'),
  ]);

  // === 05 Opatření ===
  const [opatreni, setOpatreni] = useState<OpatreniRow[]>(() => OPATRENI_DEFAULT.map(opatreniRow));
  const [mereni, setMereni] = useState('');

  // === 06 Hasební prostředky ===
  const [hasiva, setHasiva] = useState(HASIVA_DEFAULT);
  const [jineHaseni, setJineHaseni] = useState('');

  // === 07 Požární dohled ===
  const [forma, setForma] = useState<'neprerusene' | 'intervaly'>('neprerusene');
  const [intervalMin, setIntervalMin] = useState('');
  const [doba, setDoba] = useState(String(MIN_DOBA_H));
  const [vyjimka, setVyjimka] = useState('');
  const [osobaBehem, setOsobaBehem] = useState('');
  const [osobaPo, setOsobaPo] = useState('');
  const [povinnosti, setPovinnosti] = useState(POVINNOSTI_DEFAULT);

  // === 08 Uložení soupravy ===
  const [ulozeni, setUlozeni] = useState('');

  // === 09 Průběh dohledu ===
  const [kontroly, setKontroly] = useState<KontrolaRow[]>(() => [emptyKontrola()]);

  // === 10 Předání a ukončení ===
  const [predal, setPredal] = useState('');
  const [prevzal, setPrevzal] = useState('');
  const [predaniCas, setPredaniCas] = useState('');
  const [konecDatum, setKonecDatum] = useState('');
  const [konecCas, setKonecCas] = useState('');
  const [zaver, setZaver] = useState(ZAVER_DEFAULT);

  // === Podpisy ===
  const [podpisy, setPodpisy] = useState({
    vydal: { jmeno: '', funkce: 'stavbyvedoucí', image: '' },
    dohled: { jmeno: '', funkce: 'osoba požárního dohledu', image: '' },
    prevzal: { jmeno: '', funkce: 'převzal pracoviště', image: '' },
  });

  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  // Aktivní stavba ze sidebaru → předvyplnit, dokud je formulář prázdný.
  useEffect(() => {
    if (!activeStavba) return;
    setStavba((s) =>
      s.nazev
        ? s
        : { nazev: activeStavba.nazev, adresa: activeStavba.adresa ?? '', zakazka: activeStavba.zakazka_cislo ?? '' },
    );
  }, [activeStavba]);

  function applyStavbaPick(p: StavbaPickResult) {
    setStavba({ nazev: p.nazev, adresa: p.adresa, zakazka: p.zakazka_cislo ?? '' });
    setActiveStavbaId(p.id);
  }

  // === Snapshot (archiv ?id= / koncept) ===
  function applySnapshot(d: Record<string, unknown>) {
    const str = (v: unknown) => (v == null ? '' : String(v));
    if (d.cislo != null) setCislo(str(d.cislo));
    if (d.datum != null) setDatum(str(d.datum));
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
    if (d.zhotovitel) setZhotovitel((z) => ({ ...z, ...(d.zhotovitel as Partial<typeof zhotovitel>) }));
    if (Array.isArray(d.druhy)) setDruhy((d.druhy as unknown[]).map(str));
    if (d.misto != null) setMisto(str(d.misto));
    if (d.ukol != null) setUkol(str(d.ukol));
    if (Array.isArray(d.techVlastni))
      setTechVlastni(
        (d.techVlastni as Array<Record<string, unknown>>).map((c) => ({ id: str(c.id), label: str(c.label) })),
      );
    if (Array.isArray(d.technologie)) setTechnologie((d.technologie as unknown[]).map(str));
    if (d.pracoviste === 'prechodne' || d.pracoviste === 'stale') setPracoviste(d.pracoviste);
    if (d.vyska != null) setVyska(str(d.vyska));
    if (d.zahajeniDatum != null) setZahajeniDatum(str(d.zahajeniDatum));
    if (d.zahajeniCas != null) setZahajeniCas(str(d.zahajeniCas));
    if (d.ukonceniDatum != null) setUkonceniDatum(str(d.ukonceniDatum));
    if (d.ukonceniCas != null) setUkonceniCas(str(d.ukonceniCas));
    if (d.ohlasenoKomu != null) setOhlasenoKomu(str(d.ohlasenoKomu));
    if (Array.isArray(d.ucastnici)) {
      const list = (d.ucastnici as Array<Record<string, unknown>>).map((r) => ({
        id: newId('u'),
        role: str(r.role),
        jmeno: str(r.jmeno),
        doklad: str(r.doklad),
        firma: str(r.firma),
        podpis: str(r.podpis),
      }));
      setUcastnici(list.length ? list : [emptyUcastnik()]);
    }
    if (Array.isArray(d.opatreni)) {
      setOpatreni(
        (d.opatreni as Array<Record<string, unknown>>).map((r) => ({
          id: newId('o'),
          text: str(r.text),
          splneno: r.splneno === true,
          provedl: str(r.provedl),
          kontroloval: str(r.kontroloval),
        })),
      );
    }
    if (d.mereni != null) setMereni(str(d.mereni));
    if (d.hasiva != null) setHasiva(str(d.hasiva));
    if (d.jineHaseni != null) setJineHaseni(str(d.jineHaseni));
    if (d.forma === 'neprerusene' || d.forma === 'intervaly') setForma(d.forma);
    if (d.intervalMin != null) setIntervalMin(str(d.intervalMin));
    if (d.doba != null) setDoba(str(d.doba));
    if (d.vyjimka != null) setVyjimka(str(d.vyjimka));
    if (d.osobaBehem != null) setOsobaBehem(str(d.osobaBehem));
    if (d.osobaPo != null) setOsobaPo(str(d.osobaPo));
    if (d.povinnosti != null) setPovinnosti(str(d.povinnosti));
    if (d.ulozeni != null) setUlozeni(str(d.ulozeni));
    if (Array.isArray(d.kontroly)) {
      const list = (d.kontroly as Array<Record<string, unknown>>).map((r) => ({
        id: newId('k'),
        datum: str(r.datum),
        cas: str(r.cas),
        misto: str(r.misto),
        zjisteni: str(r.zjisteni),
        opatreni: str(r.opatreni),
        podpis: str(r.podpis),
      }));
      setKontroly(list.length ? list : [emptyKontrola()]);
    }
    if (d.predal != null) setPredal(str(d.predal));
    if (d.prevzal != null) setPrevzal(str(d.prevzal));
    if (d.predaniCas != null) setPredaniCas(str(d.predaniCas));
    if (d.konecDatum != null) setKonecDatum(str(d.konecDatum));
    if (d.konecCas != null) setKonecCas(str(d.konecCas));
    if (d.zaver != null) setZaver(str(d.zaver));
    if (d.podpisy) {
      const p = d.podpisy as Partial<typeof podpisy>;
      setPodpisy((cur) => ({
        vydal: { ...cur.vydal, ...(p.vydal ?? {}) },
        dohled: { ...cur.dohled, ...(p.dohled ?? {}) },
        prevzal: { ...cur.prevzal, ...(p.prevzal ?? {}) },
      }));
    }
    rows.apply(d.radky);
  }

  const docData = {
    cislo, datum, stavba, zhotovitel,
    druhy, misto, ukol, techVlastni, technologie, pracoviste, vyska,
    zahajeniDatum, zahajeniCas, ukonceniDatum, ukonceniCas, ohlasenoKomu,
    ucastnici: ucastnici.map(({ role, jmeno, doklad, firma, podpis }) => ({ role, jmeno, doklad, firma, podpis })),
    opatreni: opatreni.map(({ text, splneno, provedl, kontroloval }) => ({ text, splneno, provedl, kontroloval })),
    mereni, hasiva, jineHaseni,
    forma, intervalMin, doba, vyjimka, osobaBehem, osobaPo, povinnosti,
    ulozeni,
    kontroly: kontroly.map(({ datum: dt, cas, misto: m, zjisteni, opatreni: o, podpis }) => ({
      datum: dt, cas, misto: m, zjisteni, opatreni: o, podpis,
    })),
    predal, prevzal, predaniCas, konecDatum, konecCas, zaver,
    podpisy,
    radky: rows.snapshot(),
  };

  const { editId } = useDocEditor(applySnapshot);

  // Autosave konceptu do prohlížeče (jen u nového dokumentu).
  const draft = useDocDraft<Record<string, unknown>>(DRAFT_KEY, editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cislo, datum, stavba, zhotovitel, druhy, misto, ukol, techVlastni, technologie, pracoviste, vyska,
    zahajeniDatum, zahajeniCas, ukonceniDatum, ukonceniCas, ohlasenoKomu, ucastnici, opatreni, mereni,
    hasiva, jineHaseni, forma, intervalMin, doba, vyjimka, osobaBehem, osobaPo, povinnosti, ulozeni,
    kontroly, predal, prevzal, predaniCas, konecDatum, konecCas, zaver, podpisy, rows,
  ]);

  // === Odvozené hodnoty ===
  const vyskaNum = num(vyska);
  const pasmo = vyskaNum != null ? ochrannePasmo(vyskaNum) : null;
  const dobaNum = num(doba);
  const dobaKratka = !vyjimka && dobaNum != null && dobaNum < MIN_DOBA_H;
  const ukonceniTs = parseDateTime(ukonceniDatum, ukonceniCas)?.getTime() ?? null;
  const konecNejdrive = useMemo(() => {
    if (ukonceniTs == null || vyjimka) return null;
    const h = Math.max(dobaNum ?? MIN_DOBA_H, MIN_DOBA_H);
    return new Date(ukonceniTs + h * 3600 * 1000);
  }, [ukonceniTs, dobaNum, vyjimka]);
  const konec = parseDateTime(konecDatum, konecCas);
  const konecPredcasny = !!(konec && konecNejdrive && konec.getTime() < konecNejdrive.getTime());

  // === Tabulky ===
  const patchUcastnik = (id: string, p: Partial<UcastnikRow>) =>
    setUcastnici((l) => l.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const patchOpatreni = (id: string, p: Partial<OpatreniRow>) =>
    setOpatreni((l) => l.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const patchKontrola = (id: string, p: Partial<KontrolaRow>) =>
    setKontroly((l) => l.map((r) => (r.id === id ? { ...r, ...p } : r)));
  function kontrolaTed() {
    const row = { ...emptyKontrola(), datum: todayCz(), cas: nowHm() };
    setKontroly((l) => {
      // Prázdný poslední řádek přepiš, jinak přidej na konec.
      const last = l[l.length - 1];
      if (last && !last.datum && !last.cas && !last.misto && !last.zjisteni && !last.opatreni && !last.podpis) {
        return [...l.slice(0, -1), row];
      }
      return [...l, row];
    });
  }

  // === Chipy ===
  const techAll: Chip[] = [...TECHNOLOGIE_DEFAULT, ...techVlastni];
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  // === Uložení ===
  const title = `Požární dohled${cislo ? ' ' + cislo : ''} — ${stavba.nazev || activeStavba?.nazev || 'stavba'} (${datum})`;
  async function handleSave() {
    try {
      await saveDoc.mutateAsync({
        id: editId ?? undefined,
        doc_type: 'pozarni-dohled',
        title,
        stavba: stavba.nazev || activeStavba?.nazev,
        firma: zhotovitel.firma,
        data_json: docData,
      });
      draft.clear();
      toast.success('Požární dohled uložen do archivu.');
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
    }
  }

  const hotovo = !!(konecDatum && konecCas);
  const pdfName = `Pozarni_dohled_${cislo || stavba.nazev || 'stavba'}.pdf`;
  const ted = (setD: (v: string) => void, setT: (v: string) => void) => () => {
    setD(todayCz());
    setT(nowHm());
  };

  return (
    <>
      <DocLayout
        breadcrumb={['BOZP', 'Požární dohled']}
        title="Požární dohled"
        status={hotovo ? 'done' : 'draft'}
        statusText={
          hotovo ? 'Dohled ukončen' : ukonceniTs != null ? 'Probíhá dohled po skončení prací' : 'Rozpracovaný záznam'
        }
        actions={
          <>
            <NewDocButton draftKey={DRAFT_KEY} path={PATH} />
            <SavedDocsButton
              docType="pozarni-dohled"
              title="Uložené záznamy požárního dohledu"
              currentId={editId}
              path={PATH}
            />
            <Button variant="secondary" leading="⎙" onClick={() => printToPdf(pdfName)}>
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        {draft.pending && (
          <DraftBanner savedAt={draft.pending.savedAt} onRestore={restoreDraft} onDiscard={draft.clear} />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: deriveInitials(zhotovitel.firma),
              name: zhotovitel.firma || '—',
              address:
                [zhotovitel.sidlo, zhotovitel.ico ? `IČO ${zhotovitel.ico}` : ''].filter(Boolean).join(' · ') || '—',
            }}
            tag="Požární dohled"
            title={
              <>
                Požární dohled při svařování a práci s otevřeným ohněm{cislo ? ` č. ${cislo}` : ''}
                <span style={{ display: 'block', fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                  Zvláštní požárně bezpečnostní opatření a záznam o požárním dohledu dle vyhl. č. 87/2000 Sb.
                </span>
              </>
            }
            meta={`Stavba: ${stavba.nazev || '—'} · Vydáno ${datum || '—'}`}
            footer={{
              left: zhotovitel.firma || '—',
              center: 'vyhl. č. 87/2000 Sb. · zák. č. 133/1985 Sb. · vyhl. č. 246/2001 Sb.',
              right: stavba.zakazka ? `Zakázka: ${stavba.zakazka}` : '',
            }}
          >
            {/* 01 — Identifikace */}
            <DocBlock
              idx="01"
              title="Stavba a zhotovitel"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s01" />
                  <StavbaPickerButton title="Načíst stavbu ze seznamu" label="Stavby" onPick={applyStavbaPick} />
                  <SaveStavbaButton
                    title="Uložit stavbu do adresáře staveb"
                    build={() => ({
                      nazev: stavba.nazev.trim(),
                      adresa: stavba.adresa.trim(),
                      zakazka_cislo: stavba.zakazka.trim() || undefined,
                      datum_zahajeni: '',
                    })}
                  />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setZhotovitel({
                        firma: c.nazev,
                        ico: c.ico,
                        sidlo: c.adresa,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: zhotovitel.firma,
                      ico: zhotovitel.ico,
                      adresa: zhotovitel.sidlo,
                      kontakt_tel: zhotovitel.tel,
                      kontakt_email: zhotovitel.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo záznamu / příkazu"
                  mono
                  value={cislo}
                  onChange={setCislo}
                  placeholder="např. PD-2026/01"
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.datum') && (
                <DocRow
                  k="Datum vydání"
                  mono
                  value={datum}
                  onChange={setDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={datum} onChange={setDatum} />}
                  onRemove={() => hide('s01.datum')}
                />
              )}
              {!isHidden('s01.stavba') && (
                <DocRow
                  k="Stavba"
                  value={stavba.nazev}
                  onChange={(v) => setStavba((s) => ({ ...s, nazev: v }))}
                  placeholder="Název stavby"
                  onRemove={() => hide('s01.stavba')}
                />
              )}
              {!isHidden('s01.adresa') && (
                <DocRow
                  k="Adresa staveniště"
                  value={stavba.adresa}
                  onChange={(v) => setStavba((s) => ({ ...s, adresa: v }))}
                  onRemove={() => hide('s01.adresa')}
                />
              )}
              {!isHidden('s01.zakazka') && (
                <DocRow
                  k="Zakázka"
                  mono
                  value={stavba.zakazka}
                  onChange={(v) => setStavba((s) => ({ ...s, zakazka: v }))}
                  onRemove={() => hide('s01.zakazka')}
                />
              )}
              {!isHidden('s01.firma') && (
                <DocRow
                  k="Zhotovitel"
                  v={
                    <DocInlineAresName
                      value={zhotovitel.firma}
                      onChange={(v) => setZhotovitel((z) => ({ ...z, firma: v }))}
                      onPick={(d: AresPickResult) =>
                        setZhotovitel((z) => ({ ...z, firma: d.nazev, ico: d.ico, sidlo: d.adresa }))
                      }
                    />
                  }
                  onRemove={() => hide('s01.firma')}
                />
              )}
              {!isHidden('s01.ico') && (
                <DocRow
                  k="IČO"
                  mono
                  value={zhotovitel.ico}
                  onChange={(v) => setZhotovitel((z) => ({ ...z, ico: v }))}
                  actions={
                    <DocInlineAresLookup
                      ico={zhotovitel.ico}
                      onResult={(d) => setZhotovitel((z) => ({ ...z, firma: d.nazev, ico: d.ico, sidlo: d.adresa }))}
                    />
                  }
                  onRemove={() => hide('s01.ico')}
                />
              )}
              {!isHidden('s01.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={zhotovitel.sidlo}
                  onChange={(v) => setZhotovitel((z) => ({ ...z, sidlo: v }))}
                  onRemove={() => hide('s01.sidlo')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            {/* 02 — Místo a pracovní úkol (příl. 1 písm. a–c) */}
            <DocBlock
              idx="02"
              title="Místo, pracovní úkol a technologie"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={<DocAddRowButton rows={rows} section="s02" />}
            >
              {!isHidden('s02.druh') && (
                <DocRow
                  k="Druh činnosti"
                  v={
                    <DocInlineChips
                      items={DRUHY.map((d) => ({ id: d.id, label: d.label, checked: druhy.includes(d.id) }))}
                      onToggle={(id) => setDruhy((l) => toggle(l, id))}
                    />
                  }
                  onRemove={() => hide('s02.druh')}
                />
              )}
              {!isHidden('s02.misto') && (
                <DocRow
                  k="Přesné místo (a)"
                  wrap
                  value={misto}
                  onChange={setMisto}
                  placeholder="Objekt, podlaží, místnost / úsek, osa…"
                  onRemove={() => hide('s02.misto')}
                />
              )}
              {!isHidden('s02.ukol') && (
                <DocRow
                  k="Pracovní úkol (b)"
                  wrap
                  value={ukol}
                  onChange={setUkol}
                  placeholder="Co přesně se bude svařovat / natavovat / řezat"
                  onRemove={() => hide('s02.ukol')}
                />
              )}
              {!isHidden('s02.tech') && (
                <DocRow
                  k="Technologie (c)"
                  v={
                    <DocInlineChips
                      items={techAll.map((t) => ({ id: t.id, label: t.label, checked: technologie.includes(t.id) }))}
                      onToggle={(id) => setTechnologie((l) => toggle(l, id))}
                      onAdd={(label) => {
                        const id = newId('t');
                        setTechVlastni((l) => [...l, { id, label }]);
                        setTechnologie((l) => [...l, id]);
                      }}
                      onRemove={(id) => {
                        setTechVlastni((l) => l.filter((t) => t.id !== id));
                        setTechnologie((l) => l.filter((x) => x !== id));
                      }}
                      fixedIds={TECHNOLOGIE_DEFAULT.map((t) => t.id)}
                    />
                  }
                  onRemove={() => hide('s02.tech')}
                />
              )}
              {!isHidden('s02.pracoviste') && (
                <DocRow
                  k="Svářečské pracoviště"
                  v={
                    <select
                      className={styles.inlineSelect}
                      value={pracoviste}
                      onChange={(e) => setPracoviste(e.target.value as 'prechodne' | 'stale')}
                      aria-label="Druh svářečského pracoviště"
                    >
                      <option value="prechodne">přechodné (staveniště)</option>
                      <option value="stale">stálé (určené projektovou dokumentací)</option>
                    </select>
                  }
                  onRemove={() => hide('s02.pracoviste')}
                />
              )}
              {!isHidden('s02.vyska') && (
                <DocRow
                  k="Výška nad chráněným místem [m]"
                  v={
                    <div>
                      <input
                        className={styles.cell}
                        style={{ maxWidth: 120, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                        value={vyska}
                        onChange={(e) => setVyska(e.target.value)}
                        placeholder="např. 4,5"
                        inputMode="decimal"
                        aria-label="Výška svařování v metrech"
                      />
                      {pasmo && (
                        <span className={styles.hint}>
                          {pasmo.pod2 && 'Pod 2 m výšky se ochranné pásmo dle § 5 odst. 8 nestanovuje výpočtem.'}
                          {pasmo.nad20 && 'Nad 20 m se ochranné pásmo stanoví individuálně (§ 5 odst. 8).'}
                          {pasmo.r != null && (
                            <>
                              Ochranné pásmo (§ 5 odst. 8): kruh o poloměru min.{' '}
                              <span className={styles.calc}>{fmtNum(pasmo.r)} m</span> se středem pod místem
                              svařování.
                            </>
                          )}
                          {technologie.includes('rezani') &&
                            ' Při řezání kyslíkem a proudění vzduchu nad 1 m/s se rozšiřuje na elipsu až 20 m.'}
                        </span>
                      )}
                    </div>
                  }
                  onRemove={() => hide('s02.vyska')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            {/* 03 — Doba prací (písm. e, f, l) */}
            <DocBlock
              idx="03"
              title="Zahájení a ukončení prací"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={<DocAddRowButton rows={rows} section="s03" />}
            >
              {!isHidden('s03.zahajeni') && (
                <DocRow
                  k="Zahájení (e) — datum"
                  mono
                  value={zahajeniDatum}
                  onChange={setZahajeniDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={zahajeniDatum} onChange={setZahajeniDatum} />}
                  onRemove={() => hide('s03.zahajeni')}
                />
              )}
              {!isHidden('s03.zahajeniCas') && (
                <DocRow
                  k="Zahájení — čas"
                  mono
                  value={zahajeniCas}
                  onChange={setZahajeniCas}
                  placeholder="hh:mm"
                  actions={
                    <DocActionButton onClick={ted(setZahajeniDatum, setZahajeniCas)} title="Doplnit aktuální datum a čas">
                      Teď
                    </DocActionButton>
                  }
                  onRemove={() => hide('s03.zahajeniCas')}
                />
              )}
              {!isHidden('s03.ukonceni') && (
                <DocRow
                  k="Ukončení (f) — datum"
                  mono
                  value={ukonceniDatum}
                  onChange={setUkonceniDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={ukonceniDatum} onChange={setUkonceniDatum} />}
                  onRemove={() => hide('s03.ukonceni')}
                />
              )}
              {!isHidden('s03.ukonceniCas') && (
                <DocRow
                  k="Ukončení — čas"
                  mono
                  value={ukonceniCas}
                  onChange={setUkonceniCas}
                  placeholder="hh:mm"
                  actions={
                    <DocActionButton onClick={ted(setUkonceniDatum, setUkonceniCas)} title="Doplnit aktuální datum a čas">
                      Teď
                    </DocActionButton>
                  }
                  onRemove={() => hide('s03.ukonceniCas')}
                />
              )}
              {!isHidden('s03.ohlaseno') && (
                <DocRow
                  k="Skončení ohlášeno komu (l)"
                  value={ohlasenoKomu}
                  onChange={setOhlasenoKomu}
                  placeholder="Jméno osoby, které svářeč ohlásí skončení"
                  onRemove={() => hide('s03.ohlaseno')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            {/* 04 — Účastníci (písm. d, h, i) */}
            <DocBlock
              idx="04"
              title="Svářeči a osoby požárního dohledu — seznámení"
              actions={
                <DocActionButton onClick={() => setUcastnici((l) => [...l, emptyUcastnik()])} title="Přidat osobu">
                  + Osoba
                </DocActionButton>
              }
            >
              <p className={styles.legalNote} style={{ margin: '0 0 8px' }}>
                Podpisem účastníci potvrzují, že byli před zahájením prací seznámeni s požárně bezpečnostními
                opatřeními a osoby požárního dohledu se svými povinnostmi (příl. 1 písm. i).
              </p>
              <div className={styles.tableScroll}>
                <table className={styles.tbl}>
                  <thead>
                    <tr>
                      <th className={styles.num}>#</th>
                      <th style={{ width: 130 }}>Role</th>
                      <th>Jméno a příjmení</th>
                      <th>Doklad o odborné způsobilosti</th>
                      <th>Firma</th>
                      <th style={{ width: 110 }}>Podpis</th>
                      <th className={`${styles.del} docNoPrint`} />
                    </tr>
                  </thead>
                  <tbody>
                    {ucastnici.map((r, i) => (
                      <tr key={r.id}>
                        <td className={styles.num}>{i + 1}</td>
                        <td>
                          <select
                            className={styles.cell}
                            value={r.role}
                            onChange={(e) => patchUcastnik(r.id, { role: e.target.value })}
                            aria-label="Role"
                          >
                            {r.role && !ROLE.includes(r.role) && <option value={r.role}>{r.role}</option>}
                            {ROLE.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.jmeno}
                            onChange={(e) => patchUcastnik(r.id, { jmeno: e.target.value })}
                            placeholder="Jméno"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.doklad}
                            onChange={(e) => patchUcastnik(r.id, { doklad: e.target.value })}
                            placeholder={r.role === 'svářeč' ? 'č. svářečského průkazu' : ''}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.firma}
                            onChange={(e) => patchUcastnik(r.id, { firma: e.target.value })}
                            placeholder="Firma"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.podpis}
                            onChange={(e) => patchUcastnik(r.id, { podpis: e.target.value })}
                          />
                        </td>
                        <td className={`${styles.del} docNoPrint`}>
                          <button
                            type="button"
                            className={styles.delBtn}
                            onClick={() => setUcastnici((l) => (l.length > 1 ? l.filter((x) => x.id !== r.id) : l))}
                            title="Odebrat osobu"
                            aria-label="Odebrat osobu"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DocBlock>

            {/* 05 — Opatření (písm. g) */}
            <DocBlock
              idx="05"
              title="Požárně bezpečnostní opatření"
              actions={
                <DocActionButton onClick={() => setOpatreni((l) => [...l, opatreniRow('')])} title="Přidat opatření">
                  + Opatření
                </DocActionButton>
              }
            >
              <div className={styles.tableScroll}>
                <table className={styles.tbl}>
                  <thead>
                    <tr>
                      <th className={styles.num}>#</th>
                      <th>Opatření</th>
                      <th className={styles.check}>Splněno</th>
                      <th style={{ width: 130 }}>Provedl</th>
                      <th style={{ width: 130 }}>Zkontroloval</th>
                      <th className={`${styles.del} docNoPrint`} />
                    </tr>
                  </thead>
                  <tbody>
                    {opatreni.map((r, i) => (
                      <tr key={r.id}>
                        <td className={styles.num}>{i + 1}</td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.text}
                            onChange={(e) => patchOpatreni(r.id, { text: e.target.value })}
                            placeholder="Popis opatření"
                          />
                        </td>
                        <td className={styles.check}>
                          <input
                            type="checkbox"
                            className={styles.checkBox}
                            checked={r.splneno}
                            onChange={(e) => patchOpatreni(r.id, { splneno: e.target.checked })}
                            aria-label="Splněno"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.provedl}
                            onChange={(e) => patchOpatreni(r.id, { provedl: e.target.value })}
                            placeholder="Jméno"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.kontroloval}
                            onChange={(e) => patchOpatreni(r.id, { kontroloval: e.target.value })}
                            placeholder="Jméno"
                          />
                        </td>
                        <td className={`${styles.del} docNoPrint`}>
                          <button
                            type="button"
                            className={styles.delBtn}
                            onClick={() => setOpatreni((l) => l.filter((x) => x.id !== r.id))}
                            title="Odebrat opatření"
                            aria-label="Odebrat opatření"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DocRow
                k="Měření koncentrace hořlavých látek"
                value={mereni}
                onChange={setMereni}
                placeholder="Přístroj, naměřená hodnota, čas — nebo „nevyžaduje se“"
              />
            </DocBlock>

            {/* 06 — Hasební prostředky (písm. j, k) */}
            <DocBlock
              idx="06"
              title="Hasební prostředky"
              hiddenCount={hiddenCount('s06')}
              onRestoreHidden={() => restoreSection('s06')}
              actions={<DocAddRowButton rows={rows} section="s06" />}
            >
              {!isHidden('s06.hasiva') && (
                <DocRow
                  k="Druh a počet (j)"
                  wrap
                  value={hasiva}
                  onChange={setHasiva}
                  onRemove={() => hide('s06.hasiva')}
                />
              )}
              {!isHidden('s06.jine') && (
                <DocRow
                  k="Jiné možnosti hašení (k)"
                  wrap
                  value={jineHaseni}
                  onChange={setJineHaseni}
                  placeholder="Hydrant, SHZ, EPS, přítomnost jednotky PO…"
                  onRemove={() => hide('s06.jine')}
                />
              )}
              {!isHidden('s06.tel') && (
                <DocRow k="Ohlášení požáru" mono v="HZS 150 · tísňová linka 112" onRemove={() => hide('s06.tel')} />
              )}
              <DocCustomRows rows={rows} section="s06" />
            </DocBlock>

            {/* 07 — Požární dohled (písm. h, § 4) */}
            <DocBlock
              idx="07"
              title="Požární dohled — forma, doba a osoby"
              hiddenCount={hiddenCount('s07')}
              onRestoreHidden={() => restoreSection('s07')}
              actions={<DocAddRowButton rows={rows} section="s07" />}
            >
              {!isHidden('s07.behem') && (
                <DocRow
                  k="Dohled v průběhu prací"
                  value={osobaBehem}
                  onChange={setOsobaBehem}
                  placeholder="Jméno — nepřetržitě po celou dobu svařování (§ 4 odst. 2)"
                  onRemove={() => hide('s07.behem')}
                />
              )}
              {!isHidden('s07.vyjimka') && (
                <DocRow
                  k="Dohled po skončení"
                  v={
                    <select
                      className={styles.inlineSelect}
                      value={vyjimka}
                      onChange={(e) => setVyjimka(e.target.value)}
                      aria-label="Výjimka z dohledu po skončení"
                    >
                      {VYJIMKY.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  }
                  onRemove={() => hide('s07.vyjimka')}
                />
              )}
              {!vyjimka && !isHidden('s07.po') && (
                <DocRow
                  k="Osoba dohledu po skončení"
                  value={osobaPo}
                  onChange={setOsobaPo}
                  placeholder="Jméno předem určené osoby"
                  onRemove={() => hide('s07.po')}
                />
              )}
              {!vyjimka && !isHidden('s07.forma') && (
                <DocRow
                  k="Forma dohledu po skončení"
                  v={
                    <select
                      className={styles.inlineSelect}
                      value={forma}
                      onChange={(e) => setForma(e.target.value as 'neprerusene' | 'intervaly')}
                      aria-label="Forma dohledu"
                    >
                      <option value="neprerusene">nepřetržitě</option>
                      <option value="intervaly">v intervalech</option>
                    </select>
                  }
                  onRemove={() => hide('s07.forma')}
                />
              )}
              {!vyjimka && forma === 'intervaly' && !isHidden('s07.interval') && (
                <DocRow
                  k="Interval kontrol [min]"
                  mono
                  value={intervalMin}
                  onChange={setIntervalMin}
                  placeholder="např. 30"
                  onRemove={() => hide('s07.interval')}
                />
              )}
              {!vyjimka && !isHidden('s07.doba') && (
                <DocRow
                  k="Doba dohledu po skončení [h]"
                  v={
                    <div>
                      <input
                        className={styles.cell}
                        style={{ maxWidth: 120, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                        value={doba}
                        onChange={(e) => setDoba(e.target.value)}
                        inputMode="decimal"
                        aria-label="Doba dohledu v hodinách"
                      />
                      {dobaKratka && (
                        <span className={styles.warn}>Nejkratší doba požárního dohledu je 8 hodin (§ 4 odst. 1).</span>
                      )}
                      <span className={`${styles.hint} docNoPrint`}>
                        U tepelného dělení kovů a členitých prostorů zvažte delší dobu — požár může vzniknout i po 8 h
                        (§ 4 odst. 1).
                      </span>
                      {konecNejdrive && (
                        <span className={styles.hint}>
                          Dohled nejdříve do: <span className={styles.calc}>{fmtDateTime(konecNejdrive)}</span>
                        </span>
                      )}
                    </div>
                  }
                  onRemove={() => hide('s07.doba')}
                />
              )}
              {!isHidden('s07.povinnosti') && (
                <DocRow
                  k="Povinnosti osob dohledu"
                  wrap
                  value={povinnosti}
                  onChange={setPovinnosti}
                  onRemove={() => hide('s07.povinnosti')}
                />
              )}
              <DocCustomRows rows={rows} section="s07" />
            </DocBlock>

            {/* 08 — Uložení soupravy (písm. m) */}
            <DocBlock
              idx="08"
              title="Uložení svařovací soupravy"
              hiddenCount={hiddenCount('s08')}
              onRestoreHidden={() => restoreSection('s08')}
            >
              {!isHidden('s08.ulozeni') && (
                <DocRow
                  k="Místo a podmínky (m)"
                  wrap
                  value={ulozeni}
                  onChange={setUlozeni}
                  placeholder="Kde a jak se souprava ukládá při přerušení prací a při předání pracoviště"
                  onRemove={() => hide('s08.ulozeni')}
                />
              )}
            </DocBlock>

            {/* 09 — Průběh dohledu */}
            <DocBlock
              idx="09"
              title="Záznam o průběhu požárního dohledu"
              actions={
                <>
                  <DocActionButton onClick={kontrolaTed} title="Přidat kontrolu s aktuálním datem a časem">
                    + Kontrola teď
                  </DocActionButton>
                  <DocActionButton onClick={() => setKontroly((l) => [...l, emptyKontrola()])} title="Přidat prázdný řádek">
                    + Řádek
                  </DocActionButton>
                </>
              }
            >
              <div className={styles.tableScroll}>
                <table className={styles.tbl}>
                  <thead>
                    <tr>
                      <th className={styles.num}>#</th>
                      <th style={{ width: 100 }}>Datum</th>
                      <th style={{ width: 62 }}>Čas</th>
                      <th>Kontrolované místo</th>
                      <th>Zjištění</th>
                      <th>Provedená opatření</th>
                      <th style={{ width: 100 }}>Podpis</th>
                      <th className={`${styles.del} docNoPrint`} />
                    </tr>
                  </thead>
                  <tbody>
                    {kontroly.map((r, i) => (
                      <tr key={r.id}>
                        <td className={styles.num}>{i + 1}</td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.datum}
                            onChange={(e) => patchKontrola(r.id, { datum: e.target.value })}
                            placeholder="dd. mm. rrrr"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.cas}
                            onChange={(e) => patchKontrola(r.id, { cas: e.target.value })}
                            placeholder="hh:mm"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.misto}
                            onChange={(e) => patchKontrola(r.id, { misto: e.target.value })}
                            placeholder="pracoviště / nad / pod / vedle"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.zjisteni}
                            onChange={(e) => patchKontrola(r.id, { zjisteni: e.target.value })}
                            placeholder="bez závad"
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.opatreni}
                            onChange={(e) => patchKontrola(r.id, { opatreni: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className={styles.cell}
                            value={r.podpis}
                            onChange={(e) => patchKontrola(r.id, { podpis: e.target.value })}
                          />
                        </td>
                        <td className={`${styles.del} docNoPrint`}>
                          <button
                            type="button"
                            className={styles.delBtn}
                            onClick={() => setKontroly((l) => (l.length > 1 ? l.filter((x) => x.id !== r.id) : l))}
                            title="Odebrat kontrolu"
                            aria-label="Odebrat kontrolu"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DocBlock>

            {/* 10 — Předání a ukončení (písm. o) */}
            <DocBlock
              idx="10"
              title="Předání pracoviště a ukončení dohledu"
              hiddenCount={hiddenCount('s10')}
              onRestoreHidden={() => restoreSection('s10')}
              actions={<DocAddRowButton rows={rows} section="s10" />}
            >
              {!isHidden('s10.predal') && (
                <DocRow
                  k="Pracoviště předal"
                  value={predal}
                  onChange={setPredal}
                  placeholder="Jméno (svářeč / vedoucí prací)"
                  onRemove={() => hide('s10.predal')}
                />
              )}
              {!isHidden('s10.prevzal') && (
                <DocRow
                  k="Požární dohled převzal"
                  value={prevzal}
                  onChange={setPrevzal}
                  placeholder="Jméno osoby dohledu"
                  onRemove={() => hide('s10.prevzal')}
                />
              )}
              {!isHidden('s10.predaniCas') && (
                <DocRow
                  k="Předáno — datum a čas"
                  mono
                  value={predaniCas}
                  onChange={setPredaniCas}
                  placeholder="dd. mm. rrrr hh:mm"
                  actions={
                    <DocActionButton
                      onClick={() => setPredaniCas(`${todayCz()} ${nowHm()}`)}
                      title="Doplnit aktuální datum a čas"
                    >
                      Teď
                    </DocActionButton>
                  }
                  onRemove={() => hide('s10.predaniCas')}
                />
              )}
              {!isHidden('s10.konec') && (
                <DocRow
                  k="Dohled ukončen — datum"
                  mono
                  value={konecDatum}
                  onChange={setKonecDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={konecDatum} onChange={setKonecDatum} />}
                  onRemove={() => hide('s10.konec')}
                />
              )}
              {!isHidden('s10.konecCas') && (
                <DocRow
                  k="Dohled ukončen — čas"
                  v={
                    <div>
                      <input
                        className={styles.cell}
                        style={{ maxWidth: 120, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                        value={konecCas}
                        onChange={(e) => setKonecCas(e.target.value)}
                        placeholder="hh:mm"
                        aria-label="Čas ukončení dohledu"
                      />
                      {konecPredcasny && konecNejdrive && (
                        <span className={styles.warn}>
                          Dohled ukončen před {fmtDateTime(konecNejdrive)} — kratší než stanovená / minimální doba.
                        </span>
                      )}
                    </div>
                  }
                  actions={
                    <DocActionButton onClick={ted(setKonecDatum, setKonecCas)} title="Doplnit aktuální datum a čas">
                      Teď
                    </DocActionButton>
                  }
                  onRemove={() => hide('s10.konecCas')}
                />
              )}
              {!isHidden('s10.zaver') && (
                <DocRow k="Závěr" wrap value={zaver} onChange={setZaver} onRemove={() => hide('s10.zaver')} />
              )}
              <DocCustomRows rows={rows} section="s10" />
            </DocBlock>

            {/* 11 — Podpisy (písm. n, o) */}
            <DocBlock idx="11" title="Podpisy">
              <DocRow
                k="Opatření vydal — jméno (n)"
                value={podpisy.vydal.jmeno}
                onChange={(v) => setPodpisy((p) => ({ ...p, vydal: { ...p.vydal, jmeno: v } }))}
              />
              <DocRow
                k="Opatření vydal — funkce"
                value={podpisy.vydal.funkce}
                onChange={(v) => setPodpisy((p) => ({ ...p, vydal: { ...p.vydal, funkce: v } }))}
              />
              <DocRow
                k="Osoba požárního dohledu"
                value={podpisy.dohled.jmeno}
                onChange={(v) => setPodpisy((p) => ({ ...p, dohled: { ...p.dohled, jmeno: v } }))}
                placeholder={osobaPo || osobaBehem || ''}
              />
              <DocRow
                k="Pracoviště po dohledu převzal"
                value={podpisy.prevzal.jmeno}
                onChange={(v) => setPodpisy((p) => ({ ...p, prevzal: { ...p.prevzal, jmeno: v } }))}
              />
            </DocBlock>

            <DocSignatures
              items={[
                {
                  label: 'Opatření vydal',
                  name: podpisy.vydal.jmeno || '—',
                  role: [podpisy.vydal.funkce, datum].filter(Boolean).join(' · '),
                  image: podpisy.vydal.image || undefined,
                  onSign: (v) => setPodpisy((p) => ({ ...p, vydal: { ...p.vydal, image: v } })),
                },
                {
                  label: 'Požární dohled',
                  name: podpisy.dohled.jmeno || osobaPo || osobaBehem || '—',
                  role: podpisy.dohled.funkce,
                  image: podpisy.dohled.image || undefined,
                  onSign: (v) => setPodpisy((p) => ({ ...p, dohled: { ...p.dohled, image: v } })),
                },
                {
                  label: 'Převzal pracoviště',
                  name: podpisy.prevzal.jmeno || '—',
                  role: podpisy.prevzal.funkce,
                  image: podpisy.prevzal.image || undefined,
                  onSign: (v) => setPodpisy((p) => ({ ...p, prevzal: { ...p.prevzal, image: v } })),
                },
              ]}
            />

            {/* 12 — Právní rámec */}
            <DocBlock idx="12" title="Právní rámec">
              <ul className={styles.legal}>
                <li>
                  <strong>Vyhláška č. 87/2000 Sb.</strong>, kterou se stanoví podmínky požární bezpečnosti při
                  svařování a nahřívání živic v tavných nádobách — § 2 písm. i) (pojem požární dohled), § 3 (zvláštní
                  požárně bezpečnostní opatření, účast nejméně 2 osob, podmínky zahájení), § 4 (požární dohled,
                  nejkratší doba 8 hodin, výjimky), § 5 odst. 5 (hasicí přístroje), § 5 odst. 8 (ochranná pásma),
                  příloha č. 1 (rozsah zvláštních požárně bezpečnostních opatření).
                </li>
                <li>
                  <strong>Zákon č. 133/1985 Sb.</strong>, o požární ochraně — § 4 odst. 2 písm. f) (činnost se
                  zvýšeným požárním nebezpečím při použití otevřeného ohně), § 5 a § 6 (povinnosti právnických
                  a podnikajících fyzických osob), § 13 (preventivní požární hlídka), § 16 (odborná příprava).
                </li>
                <li>
                  <strong>Vyhláška č. 246/2001 Sb.</strong>, o požární prevenci — § 15 (činnosti se zvýšeným požárním
                  nebezpečím; u dodavatelsky prováděných činností zajišťuje zhotovitel, není-li smlouvou stanoveno
                  jinak), § 24 (odborná příprava preventivních požárních hlídek), § 40 (dokumentace požární ochrany).
                </li>
                <li>
                  <strong>ČSN 05 0601</strong> Svařování. Bezpečnostní ustanovení pro svařování kovů. Provoz.
                  · <strong>ČSN 05 0610</strong> Svařování. Bezpečnostní ustanovení pro svařování a řezání kovů
                  plamenem.
                </li>
              </ul>
            </DocBlock>
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="pozarni-dohled"
        docTitle={title}
        defaultTo={zhotovitel.email}
        defaultSubject={title}
        meta={`Stavba: ${stavba.nazev || '—'}`}
      />
    </>
  );
}
